import { VerticalOption } from '@amzn/aet-galactus-kit';
import viewTokens from '@amzn/meridian-tokens/base/icon/view';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { renderUrl, urls } from '../../../Util';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';

import DataSource from '../../../api';
import { Profile, User } from '../../../Types';
import DataState from '../../../Types/DataState';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import useCustomTracking from '../../../Util/useCustomTracking';
import Filter from '../../Team/FilterComponent/Filter';
import {
  EmptyLighthouse,
  topLevelAllClose,
  topLevelAllOpen,
} from '../DashboardComponents/LighthouseMetrics/helper';
import MetricCard from '../DashboardComponents/LighthouseMetrics/MetricsCard';
import {
  GeneralMetricType,
  LighthouseFilters,
  LighthouseMetricOption,
  LighthouseMetrics,
  TimeOption,
} from '../DashboardComponents/LighthouseMetrics/types';
import {
  initVerticalCardState,
  verticalCardStateSwitch,
} from '../DashboardComponents/LighthouseMetrics/VerticalCardState';
import { EPOCH_START } from '../../../Util/GlobalConstants';
import moment from 'moment';
import { useNavigate } from 'react-router';
import {
  ActionableFeedbacks,
  GetFeedbackMessagesInput,
} from '../../../api/Dashboard/schema';
import Page from '../../../Types/Page';
import { checkGoUser } from '../../../Util/User';
import {
  checkAccessToRealTimeHmd,
  CONSUMER_VERTICALS,
  SDS_VERTICALS,
  convertToVerticalOption,
  decideVerticalOption,
} from '../../../Util/Vertical';
import PerformancePortalContext from '../../PerformancePortalContext';
import _ from 'lodash';
import useInspireOptions from '../../../hooks/useInspireOptions';
import { MetricsUseHistoricalDataModal } from '../DashboardComponents/MetricsUseHistoricalDataModal';
import { isOmViewingTheirOwnProfile } from '../helper';
import PerformanceCardWrapper from '../DashboardComponents/PerformanceCardWrapper';

const TRAILING_DAYS = 'Trailing 7 days';

/** CCX-related metric types to remove when enableCCXRemoval feature flag is active */
const CCX_METRICS_TO_REMOVE = new Set([
  LighthouseMetricOption.CCX,
  'CCX_NEG',
  'CCX_NEG_ONEBOX',
]);

interface DataContainerProps {
  user: User;
  cscEntityPrefix: string;
  isBig: boolean;
  cardTruncate: string[];
  filterTruncate: string[];
  profile: Profile;
  isProfileTm: boolean;
  displaySubtitles: string;
  achtEnabled: boolean;
  concessionEnabled: boolean;
  setWeek: Function;
  weekValue: string;
  weekOptions: string[];
  LHDefaultVertical: string;
  LHDefaultVerticalState: string;
  lighthouseFilters: LighthouseFilters;
  lighthouseFiltersState: string;
  cardWidth: string;
  displayScoreHeader: string;
  displayScoreFooter: string;
  responsiveFont: boolean;
  setSyncTimeChangeCount: Function;
}

const DataContainer: React.FC<DataContainerProps> = ({
  user,
  cscEntityPrefix,
  isBig,
  cardTruncate,
  filterTruncate,
  profile,
  isProfileTm,
  displaySubtitles,
  achtEnabled,
  concessionEnabled,
  setWeek,
  weekValue,
  weekOptions,
  LHDefaultVertical,
  LHDefaultVerticalState,
  lighthouseFilters,
  lighthouseFiltersState,
  cardWidth,
  displayScoreHeader,
  displayScoreFooter,
  responsiveFont,
  setSyncTimeChangeCount,
}: DataContainerProps): React.ReactElement => {
  /**
   *  @todo - The following values - possible filter values - we need to parse it after gettting responses from backend
   */
  /** Common */
  const apiNaws = DataSource.getDataFeed('NAWS');

  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );
  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);
  const { isTm, isOm, isExclusivelyOm, userID } = useInspireOptions();
  const enableHistoricalCCXDetails = isFeatureEnabled(
    Feature.ENABLE_HISTORICAL_CCX_DETAIL
  );
  const isGoUser = checkGoUser(profile.user.owner);
  const navigate = useNavigate();

  const csaAchtEnabled = isFeatureEnabled(Feature.CSA_ACHT);

  const { performancePortalContextValue, setPerformancePortalContextValue } =
    useContext(PerformancePortalContext);

  /** Filter Value and Options */
  const [filterVerticalValue, setFilterVerticalValue] = useState('');
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [verticalValue, setVerticalValue] = useState('');
  const [skillValue, setSkillValue] = useState('ALL');
  const [channelValue, setChannelValue] = useState('ALL');
  const [segmentValue, setSegmentValue] = useState('ALL');
  const [marketplaceValue, setMarketplaceValue] = useState('ALL');
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<string[]>([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState<string[]>([]);
  const [orgVertical, setOrgVertical] = useState(null);
  const [orgVerticalState, setOrgVerticalState] = useState(DataState.LOADING);

  const buildCCXQueryParam = () => {
    return `&verticalValue=${filterVerticalValue}&skillValue=${skillValue}&segmentValue=${segmentValue}&channelValue=${channelValue}&marketplaceValue=${marketplaceValue}&weekValue=${weekValue}`;
  };

  /** Button */
  const [expandAllOpen, setExpandAllOpen] = useState(false);
  const [isHitByButton, setIsHitByButton] = useState(false);

  /** TopLevel Metrics Value and State - Integration */
  const [lighthouseMetrics, setLighthouseMetrics] =
    useState<LighthouseMetrics>(EmptyLighthouse);
  const [lighthouseMetricsState, setLighthouseMetricsState] = useState(
    DataState.LOADING
  );

  /** SubLevel Metrics Value and State - Triggered By ExpandAll - Integration */
  const [lighthouseSubMetrics, setlighthouseSubMetrics] =
    useState<LighthouseMetrics>(EmptyLighthouse);
  const [lighthouseSubMetricsState, setlighthouseSubMetricsState] = useState(
    DataState.LOADING
  );

  // Check for Vertical is in Vertical Options or not
  const [checkOptions, setCheckOptions] = useState(false);
  const [checkState, setCheckState] = useState(DataState.LOADING);

  const [latestMetricPublishDate, setLatestMetricPublishDate] =
    useState(EPOCH_START);

  const [feedbackMsgs, setFeedbackMsgs] = useState<ActionableFeedbacks[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [feedbackMsgsState, setFeedbackMsgsState] = useState(DataState.LOADING);

  /** Card State */
  const [cardState, dispatch] = useReducer(
    verticalCardStateSwitch,
    initVerticalCardState
  );

  // Custom Instrumentation for Collapse Button
  const [CollapseAllTrackingTag, CollapseAllTrackClick] = useCustomTracking(
    'lighthouse-metric-collapseAll-button-slot',
    'lighthouse-metric-collapseAll-button',
    {
      'data-csa-c-type': 'button',
    }
  );

  useEffect(() => {
    if (
      setPerformancePortalContextValue &&
      !performancePortalContextValue?.dashboardFilters
    ) {
      setPerformancePortalContextValue({
        ...performancePortalContextValue,
        dashboardFilters: {
          ...performancePortalContextValue?.dashboardFilters,
          weekValue,
        },
      });
    }
  }, []);

  const setVerticalAndStateInit = (vertical: any) => {
    dispatch({
      type: 'init',
      vertical: convertToVerticalOption(vertical),
    });
  };

  useEffect(() => {
    if (filterVerticalValue === '') return;
    setVerticalValue(
      decideVerticalOption(
        filterVerticalValue || LHDefaultVertical,
        isGoUser,
        enableRAPRemovalAndCCXUpdate
      )
    );
  }, [
    filterVerticalValue,
    isGoUser,
    LHDefaultVertical,
    enableRAPRemovalAndCCXUpdate,
  ]);

  useEffect(() => {
    dispatch({
      type: 'init',
      vertical: verticalValue,
    });
  }, [verticalValue]);

  // Initialize Card and Button State
  useEffect(() => {
    setIsHitByButton(false);
    setlighthouseSubMetrics(EmptyLighthouse);
    setlighthouseSubMetricsState(DataState.LOADING);
  }, [
    weekValue,
    verticalValue,
    skillValue,
    channelValue,
    segmentValue,
    marketplaceValue,
  ]);

  /**
   * @ToDo based on the mocks given for No data implementation, we could remove the extra query made to the backend
   * Handle the case for Vertical Options and Default Vertical
   * */

  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    /**
     * @ToDo - This logic is based on Aggregated is first element - It is first element for now
     * If we have other vertical which has higher rank of Total - then we need to think how we set verticalValue
     * when default vertical is null
     * */
    if (
      LHDefaultVertical === null ||
      !lighthouseFilters.verticals.includes(LHDefaultVertical)
    ) {
      setFilterVerticalValue(VerticalOption.Total);
    } else {
      setFilterVerticalValue(convertToVerticalOption(LHDefaultVertical));
    }
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  // Initalize Vertical-Card-State Map
  useEffect(() => {
    if (verticalValue === '') return;
    dispatch({
      type: 'init',
      vertical: verticalValue,
    });
  }, [verticalValue]);

  // Initialize Card and Button State
  useEffect(() => {
    if (verticalValue === '') return;
    setIsHitByButton(false);
    setlighthouseSubMetrics(EmptyLighthouse);
    setlighthouseSubMetricsState(DataState.LOADING);
  }, [
    weekValue,
    verticalValue,
    skillValue,
    channelValue,
    segmentValue,
    marketplaceValue,
  ]);

  useEffect(() => {
    if (
      user.userID === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    const userIds = [`${cscEntityPrefix}:${user.userID}`];
    const queryVariables = apiNaws.prepareOrgVerticalQuery(
      userIds,
      `${cscEntityPrefix}:${user.userID}`
    );

    apiNaws.getOrgVertical(
      queryVariables,
      setOrgVertical,
      setOrgVerticalState,
      'cache-first'
    );
  }, [
    user.userID,
    lighthouseFiltersState,
    LHDefaultVerticalState,
    cscEntityPrefix,
  ]);

  //Check Default Vertical is in lighthouse Filters or not
  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (!LHDefaultVertical) {
      setCheckOptions(false);
      setCheckState(DataState.LOADED);
      return;
    }
    setCheckOptions(lighthouseFilters.verticals.includes(LHDefaultVertical));
    setCheckState(DataState.LOADED);
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  // Set Filter Options
  useEffect(() => {
    if (
      user.userID === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    // @ToDo - If filter options are null, we don't want to send a query when we click the link
    setSkillOptions(lighthouseFilters.skills);
    setChannelOptions(lighthouseFilters.channels);
    setSegmentOptions(lighthouseFilters.segments);
    // ToDo - Remove question marks after the release
    setMarketplaceOptions(lighthouseFilters.marketplaces ?? []);

    /**
     * Display Total as Aggregated - Make new array in the Order, that Total at first index
     */
    const verticalParam = lighthouseFilters.verticals;

    const tempVertical = [VerticalOption.Total, ...verticalParam];
    const sortedVertical = [...new Set(tempVertical)]; // Total is not default vertical - Regard it as Aggregated

    setVerticalOptions(sortedVertical);
  }, [lighthouseFiltersState, LHDefaultVerticalState, user.userID]);

  // Reset check value to false and state to Loaded - since Default Check is used for the first time
  useEffect(() => {
    if (verticalValue === '') return;
    setCheckOptions(false);
    setCheckState(DataState.LOADED);
  }, [verticalValue, skillValue, channelValue, segmentValue, marketplaceValue]);

  // Lighthouse Metrics Query
  useEffect(() => {
    if (
      user.userID === '' ||
      verticalValue === '' ||
      checkState === DataState.LOADING ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    /**
     * 1. If Default vertical does exist in the filterOptions, then we pass variable in our query
     * 2. If Default vertical doesn't exist in the filterOptions, then we pass Total in our query
     *    2.1 VerticalValue Assignment has been done in another useEffect Block and when it is passed
     *        this block, it has been assigned to 'Total'
     */
    let verticalValueFromDefaultVertical = decideVerticalOption(
      LHDefaultVertical,
      isGoUser,
      enableRAPRemovalAndCCXUpdate
    );

    let verticalValueForCommonCase = convertToVerticalOption(verticalValue);

    const queryVariables = apiNaws.prepareLighthouseQuery(
      user.userID,
      cscEntityPrefix,
      isProfileTm,
      checkOptions
        ? verticalValueFromDefaultVertical
        : verticalValueForCommonCase,
      channelValue,
      skillValue,
      segmentValue,
      marketplaceValue,
      weekValue,
      weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
      [], // Metrics Array - For topLevels - we pass empty Array
      isProfileTm ? profile.directReports : []
    );

    apiNaws.getLighthouseMetrics(
      queryVariables,
      (payload: LighthouseMetrics) => {
        setLighthouseMetrics(payload);
        /*
          Wrapper component fetches the filter options, while this one fetches LM.
          The wrapper needs to refresh filter options when metric sync time is updated, 
            i.e., "payload.latestMetricPublishDate".
          We don't know the first value of sync time when filter options are fetched initially,
            so the initial value is "EPOCH_START".
          To avoid triggering an unnecessary filter options fetch 
            when metric sync time updates from EPOCH_START to <first value>,
            we check if previous sync time == EPOCH_START.
          We cannot pass the "syncTimeChangeCount" as a prop to this component,
            since a change in the prop would also render this component once more and trigger a fetch,
            hence functional state update is used instead of regular form.
        */
        setLatestMetricPublishDate(
          (prevLatestMetricPublishDate: string): string => {
            if (
              prevLatestMetricPublishDate !== EPOCH_START &&
              payload.latestMetricPublishDate !== EPOCH_START &&
              prevLatestMetricPublishDate !== payload.latestMetricPublishDate
            ) {
              setSyncTimeChangeCount((count: number) => count + 1);
            }

            return payload.latestMetricPublishDate !== EPOCH_START
              ? payload.latestMetricPublishDate
              : prevLatestMetricPublishDate;
          }
        );
      },
      setLighthouseMetricsState
    );

    setCheckState(DataState.LOADING);
    setExpandAllOpen(false);
    setVerticalAndStateInit(verticalValue);
  }, [
    user.userID,
    verticalValue,
    weekValue,
    skillValue,
    channelValue,
    lighthouseFilters,
    checkState,
    segmentValue,
    marketplaceValue,
    LHDefaultVerticalState,
    lighthouseFiltersState,
  ]);

  // Lighthouse Metrics - By 'Expand ALL'
  useEffect(() => {
    if (!isHitByButton) return;

    const verticalValueFromDefaultVertical = decideVerticalOption(
      LHDefaultVertical,
      isGoUser,
      enableRAPRemovalAndCCXUpdate
    );
    const verticalValueForCommonCase = convertToVerticalOption(verticalValue);

    const vertical = checkOptions
      ? verticalValueFromDefaultVertical
      : verticalValueForCommonCase;

    const queryVariables = apiNaws.prepareLighthouseQuery(
      user.userID,
      cscEntityPrefix,
      isProfileTm,
      vertical,
      channelValue,
      skillValue,
      segmentValue,
      marketplaceValue,
      weekValue,
      weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
      lighthouseMetrics.metricTypes,
      isProfileTm ? profile.directReports : []
    );

    apiNaws.getLighthouseMetrics(
      queryVariables,
      setlighthouseSubMetrics,
      setlighthouseSubMetricsState
    );

    setCheckState(DataState.LOADING);
  }, [isHitByButton, checkOptions]);

  const weekValueChanger = (week: any) => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
    LHDefaultVertical === null
      ? setFilterVerticalValue(VerticalOption.Total)
      : setFilterVerticalValue(LHDefaultVertical);
    setWeek(week);
  };

  const realTimeHMD = (): void => {
    navigate(renderUrl(`${urls.realTimeHmdDetails}/${user.userID}`), {
      state: { profile },
    });
  };

  useEffect(() => {
    topLevelAllClose(
      Object.values(cardState),
      setExpandAllOpen,
      setlighthouseSubMetricsState,
      setIsHitByButton,
      expandAllOpen
    );
  }, [cardState]);

  useEffect(() => {
    if (verticalValue === '') return;
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [verticalValue]);

  const isOmViewingOwnProfile = isOmViewingTheirOwnProfile(
    isOm,
    userID,
    profile.user.userID
  );

  return (
    <>
      <MetricsUseHistoricalDataModal weekValue={weekValue} isManager={isTm} />
      <Row alignmentHorizontal='justify'>
        {isProfileTm ? (
          <Text type='h400'>
            <Row>
              {isOmViewingOwnProfile ? (
                <div data-cy='tm_team performance'>
                  <FormattedMessage
                    id='cs_perfportal_tm_team_performance'
                    defaultMessage='TM Team Performance'
                  />
                </div>
              ) : (
                <div data-cy='team performance'>
                  <FormattedMessage
                    id='cs_perfportal_team_performance'
                    defaultMessage='Team Performance'
                  />
                </div>
              )}
            </Row>
          </Text>
        ) : (
          <Text type='h400'>
            <div data-cy='performance'>
              <FormattedMessage
                id='cs_perfportal_performance'
                defaultMessage='Performance'
              />
            </div>
          </Text>
        )}
        {LHDefaultVerticalState == DataState.LOADED &&
          orgVerticalState === DataState.LOADED &&
          filterVerticalValue &&
          (!enableHistoricalCCXDetails ||
            (enableHistoricalCCXDetails &&
              checkAccessToRealTimeHmd(
                isGoUser,
                isTm,
                convertToVerticalOption(filterVerticalValue),
                orgVertical,
                isExclusivelyOm,
                isOmViewingOwnProfile
              ))) && (
            <Row alignmentHorizontal='end'>
              <Link
                type='secondary'
                onClick={realTimeHMD}
                data-csa-c-type='link'
                data-csa-c-slot-id='open-real-time-hmd-modal'
                data-csa-c-content-id='open-real-time-hmd-modal'
                data-csa-c-description='Navigate to real time hmd'
                data-csa-c-profile-id={`${profile.user.userID}`}
                data-csa-c-tag1='Dashboard'
              >
                <FormattedMessage
                  id='cs_perfportal_realTimeHmdDetails_title'
                  defaultMessage='Real Time HMD Details'
                />
                <Icon tokens={chevronRightSmallTokens} />
              </Link>
            </Row>
          )}
      </Row>
      <Box type='fill' spacingInset='400'>
        {(lighthouseFiltersState === DataState.LOADING ||
          LHDefaultVerticalState === DataState.LOADING) && (
          <div style={{ textAlign: 'center' }}>
            <Loader type='circular' size='large' />
          </div>
        )}
        {lighthouseFiltersState === DataState.LOADED &&
          LHDefaultVerticalState === DataState.LOADED && (
            <Row
              width='100%'
              wrap='down'
              alignmentVertical='center'
              alignmentHorizontal='justify'
            >
              <Row wrap='down'>
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_filterBy'
                    defaultMessage='Filter by'
                  />
                </Text>

                <Filter
                  verticalValue={filterVerticalValue}
                  weekValue={weekValue}
                  skillValue={skillValue}
                  channelValue={channelValue}
                  segmentValue={segmentValue}
                  verticalOptions={verticalOptions}
                  skillOptions={skillOptions}
                  channelOptions={channelOptions}
                  segmentOptions={segmentOptions}
                  weekOptions={weekOptions}
                  filterTruncate={filterTruncate}
                  customDate={undefined}
                  dateRange={undefined}
                  setVerticalValue={setFilterVerticalValue}
                  setSkillValue={setSkillValue}
                  setChannelValue={setChannelValue}
                  weekValueChanger={weekValueChanger}
                  setSegmentValue={setSegmentValue}
                  setCustomDate={undefined}
                  setDateRange={undefined}
                  page={Page.DASHBOARD}
                  marketplaceValue={marketplaceValue}
                  marketplaceOptions={marketplaceOptions}
                  setMarketplaceValue={setMarketplaceValue}
                />
              </Row>

              {!expandAllOpen &&
              !topLevelAllOpen(Object.values(cardState), setExpandAllOpen) ? (
                <Button
                  onClick={() => {
                    setExpandAllOpen(true);
                    setIsHitByButton(true);
                    dispatch({
                      type: 'expandAll',
                      vertical: convertToVerticalOption(verticalValue),
                    });
                  }}
                  data-csa-c-type='button'
                  data-csa-c-slot-id='lighthouse-metric-expandAll-button-slot'
                  data-csa-c-content-id='lighthouse-metric-expandAll-button'
                  data-csa-c-description='Expand All Metrics Cards'
                  data-csa-c-tag1='Dashboard'
                >
                  <Icon tokens={viewTokens} />{' '}
                  <FormattedMessage
                    id='cs_perfportal_expandAll'
                    defaultMessage='Expand All'
                  />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setExpandAllOpen(false);
                      setIsHitByButton(false);
                      CollapseAllTrackClick();
                      setlighthouseSubMetricsState(DataState.LOADING);
                      dispatch({
                        type: 'init',
                        vertical: verticalValue,
                      });
                    }}
                    data-csa-c-type='button'
                    data-csa-c-slot-id='lighthouse-metric-collapseAll-button-slot'
                    data-csa-c-content-id='lighthouse-metric-collapseAll-button'
                    data-csa-c-description='Collapse All Metrics Cards'
                    data-csa-c-tag1='Dashboard'
                  >
                    <CollapseAllTrackingTag />
                    <Icon tokens={viewTokens} />{' '}
                    <FormattedMessage
                      id='cs_perfportal_collapseAll'
                      defaultMessage='Collapse All'
                    />
                  </Button>
                </>
              )}
            </Row>
          )}
        {(lighthouseFiltersState === DataState.ERROR ||
          LHDefaultVerticalState === DataState.ERROR) && (
          <Alert type='error'>
            <FormattedMessage
              id='cs_perfportal_error_refresh_page'
              defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
            />
          </Alert>
        )}
      </Box>

      {(lighthouseFiltersState === DataState.LOADING ||
        LHDefaultVerticalState === DataState.LOADING ||
        lighthouseMetricsState === DataState.LOADING) && (
        <Row
          wrap='down'
          width='100%'
          widths={'fill'}
          alignmentVertical='top'
          spacingInset='400 400'
        >
          <div style={{ textAlign: 'center' }}>
            <Loader type='circular' size='large' />
          </div>
        </Row>
      )}

      {lighthouseFiltersState === DataState.LOADED &&
        LHDefaultVerticalState === DataState.LOADED &&
        lighthouseMetricsState === DataState.LOADED && (
          <Row
            wrap='down'
            width='100%'
            widths={cardTruncate}
            alignmentVertical='top'
            spacingInset='400 none'
          >
            {lighthouseMetrics.metricTypes
              .filter(
                metric =>
                  !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
                  !(
                    CONSUMER_VERTICALS.has(
                      convertToVerticalOption(verticalValue)
                    ) &&
                    !concessionEnabled &&
                    metric === LighthouseMetricOption.CONCESSION_RATE
                  ) &&
                  !(
                    SDS_VERTICALS.has(convertToVerticalOption(verticalValue)) &&
                    metric === LighthouseMetricOption.DSR
                  ) &&
                  (lighthouseMetrics[
                    metric as keyof LighthouseMetrics
                  ]! as GeneralMetricType) &&
                  //# TODO - Remove this condition once csaAchtEnabled is enabled for all regions as currently we are blocking DE and NL
                  !(
                    metric === LighthouseMetricOption.ACHT &&
                    !isProfileTm &&
                    !csaAchtEnabled
                  ) &&
                  // Filter out CCX-related metrics for users when enableCCXRemoval is enabled
                  !(enableCCXRemoval && CCX_METRICS_TO_REMOVE.has(metric))
              )
              .map((metric: string) => {
                return (
                  <PerformanceCardWrapper
                    isBig={isBig}
                    hmdState={DataState.LOADED}
                    key={metric}
                  >
                    <MetricCard
                      type={metric}
                      user={user}
                      metrics={
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        lighthouseMetrics[
                          metric as keyof LighthouseMetrics
                        ]! as GeneralMetricType
                      }
                      displaySubtitles={displaySubtitles}
                      isProfileTm={isProfileTm}
                      expandAll={expandAllOpen}
                      dispatch={dispatch}
                      vertical={verticalValue}
                      channel={channelValue}
                      skill={skillValue}
                      segment={segmentValue}
                      marketplace={marketplaceValue}
                      week={weekValue}
                      profile={profile}
                      isHitByExpandAllButton={isHitByButton}
                      subMetrics={lighthouseSubMetrics}
                      subMetricsState={lighthouseSubMetricsState}
                      cardState={cardState}
                      setExpandAll={setExpandAllOpen}
                      cscEntityPrefix={cscEntityPrefix}
                      cardWidth={cardWidth}
                      displayScoreHeader={displayScoreHeader}
                      displayScoreFooter={displayScoreFooter}
                      responsiveFont={responsiveFont}
                      ccxQueryParam={buildCCXQueryParam()}
                    />
                  </PerformanceCardWrapper>
                );
              })}
          </Row>
        )}

      {(lighthouseFiltersState === DataState.ERROR ||
        LHDefaultVerticalState === DataState.ERROR ||
        lighthouseMetricsState === DataState.ERROR) && (
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_error_refresh_page'
            defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
          />
        </Alert>
      )}
    </>
  );
};
export default DataContainer;
